<template>
    <div class="clinic-consent-template-edit" v-loader.opacity.tab="loading">
      <div class="title-box">
        <h2 class="title">
          {{ template.id ? $t('clinic.editConsentTemplate') : $t('clinic.newConsentTemplate') }}
        </h2>
        <button class="btn-back" type="button" name="button" data-test="btnInfo" @click="$refs.tutorialModal.show()" v-if="!template.isDefault">
            {{ $t('clinic.learnMore') }}
         </button>
      </div>

      <vee-form class="form-wrapper" v-slot="{ handleSubmit }" as="div">
        <form @submit.prevent.stop="handleSubmit(doSave)" @keydown.enter="$event.preventDefault()">

          <div class="consent-template-box scrollbar">
            <div class="section-inputs">
              <div class="input-col">
                <div class="form-floating">
                  <vee-field name="name" v-model="template.name" rules="required|max:100" v-slot="{ field, errorMessage }">
                    <input v-bind="field" type="text" class="form-control" ref="name" id="ctName" :placeholder="$t('name')" autofocus :disabled="template.isDefault"/>
                    <span class="vee-error">{{ errorMessage }}</span>
                  </vee-field>
                  <label for="ctName">{{ $t('name') }}*</label>
                </div>
              </div>
              <div class="input-col">
                <div class="form-floating">
                  <vee-field name="description" v-model="template.description" rules="max:255" v-slot="{ field, errorMessage }">
                    <input v-bind="field" type="text" class="form-control" id="ctDescription" :placeholder="$t('description')" :disabled="template.isDefault"/>
                    <span class="vee-error">{{ errorMessage }}</span>
                  </vee-field>
                  <label for="ctDescription">{{ $t('description') }}</label>
                </div>
              </div>
            </div>

            <div class="section-template">
              <div class="editor-section">
                <quill-editor
                  ref="quillEditor"
                  class="editor clinic full-height-editor"
                  v-model:content="content"
                  :options="editorOption"
                  content-type="html"
                  :placeholder="$t('clinic.consentHtmlHint')"
                />
                <span class="content-error" v-if="isContentTooLong">{{ $t('validate.max', { limit: contentMaxLength }) }}</span>
              </div>
              
              <div class="variables-section">
                <div class="header-variables">
                  <label class="variables-title">{{ $t('clinic.consentFormFields') }}</label>
                  <button class="btn btn-outline-secondary btn-add-field" type="button" name="button" data-test="btnAddField" @click="addField" v-if="!template.isDefault">
                    <IconPlus />
                    <span>{{ $t('add') }}</span>
                  </button>
                </div>

                <div class="variables-list variables-fixed">
                  <div v-for="(sysVar, index) in systemVariables" :key="'sys_' + index" class="variable-item">
                    <div class="form-floating v-select-floating field-select">
                      <input type="text" class="form-control" disabled :value="sysVar.label"/>
                    </div>
                    
                    <div class="section-info">
                      <div class="tag-preview" @click="copyToClipboard('{{' + sysVar.tag + '}}')" :title="$t('copy')">
                        <span class="fw-bold" v-text="'{{' + sysVar.tag + '}}'"></span>
                      </div>
                    </div>
                  </div>
                </div>

                <div class="variables-list scrollbar" v-if="formSchema.length > 0">
                  <div v-for="(item, index) in formSchema" :key="index" class="variable-item">
                    <div class="form-floating v-select-floating field-select">
                      <vee-field :name="'variable_' + index" v-model="item.key" rules="required" v-slot="{ errorMessage }">
                        <v-select
                          :append-to-body="true"
                          class="v-select form"
                          v-model="item.key"
                          label="label"
                          :reduce="x => x.value"
                          :options="variableOptions"
                          :placeholder="$t('select')"
                          data-test="vConsentField"
                          :disabled="template.isDefault"
                        >
                          <template #search="{attributes, events}">
                            <input
                              class="vs__search"
                              v-bind="attributes"
                              v-on="events"
                            />
                          </template>
                        </v-select>
                        <span class="vee-error">{{ errorMessage }}</span>
                      </vee-field>
                      <label for="variableSelected">{{ $t('select') }}</label>
                    </div>

                    <div class="section-info">
                      <div class="tag-preview" @click="copyToClipboard('{{' + item.key + '}}')" :title="$t('copy')">
                        <span v-if="item.key" v-text="'{{' + item.key + '}}'"></span>
                        <span v-else>-</span>
                      </div>

                      <button class="btn btn-outline-secondary" type="button" name="button" v-if="!template.isDefault"
                        :data-title="$t('remove')" data-test="btnFieldRemove" @click.stop="removeField(item)">
                        <IconTrash />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
  
          <div class="action-bar" @wheel.prevent>
            <button class="btn btn-success" v-if="isGlobalClient && hasFormChanged && !template.isDefault" type="submit" name="button">
              <span>{{ $t("save") }}</span>
            </button>
            <button class="btn btn-secondary" type="button" name="button" @click="$emit('cancel-edit')">
              <span>{{ $t('cancel') }}</span>
            </button>
          </div>
        </form>
      </vee-form>

      <teleport to="body">
        <BaseTutorialModal 
          ref="tutorialModal" 
          :title="$t('tutorial.templaseConsent.title')" 
          :steps="consentTutorialSteps" 
        />
      </teleport>
    </div>
  </template>
  
<style lang="scss" scoped>
  .title-box {
    margin-bottom:                      0;
    display:                            flex !important;
    align-items:                        start;
  }
  .section-observation {
    color:                              var(--Brand-Primary);
    padding:                            10px;
    margin-bottom:                      0;
  }
  .clinic-consent-template-edit {
    display:                            flex;
    flex-direction:                     column;
    flex:                               1;
    min-height:                         0;
    overflow:                           hidden;
  }

  .form-wrapper, form {
    display:                            flex;
    flex-direction:                     column;
    flex:                               1;
    min-height:                         0;
  }

  .consent-template-box {
    // height:                           calc(100dvh - 449px); // replaced by flex: 1
    padding:                            10px;
    flex:                               1;
    overflow-y:                         auto;
    overflow-x:                         hidden;
    display:                            flex;
    flex-direction:                     column;
    gap:                                20px;
    overscroll-behavior:                contain;
  }

  .section-inputs {
    width:                              100%;
    display:                            flex;
    gap:                                20px;

    .input-col {
      flex:                             1;
      min-width:                        0;
    }
  }

  .vee-error {
    width:                              100% !important;
  }

  .content-error {
    margin-top:                         6px;
    font-size:                          0.9em;
    color:                              red;
  }

  .has-alert {
    .consent-template-box {
      // height:                         calc(100dvh - 465px); // replaced by flex: 1 in _scaffold.scss
    }
  }

  .section-template {
    display:                            flex;
    flex-direction:                     row;
    gap:                                20px;
    flex:                               1;
    align-items:                        flex-start;
  }

  .editor-section {
    flex:                               1;
    display:                            flex;
    flex-direction:                     column;
    min-width:                          0;
    min-height:                         350px;

    :deep(.ql-container) {
      display:                          flex;
      flex-direction:                   column;
      flex:                             1;
      overflow:                         hidden;
    }
    
    :deep(.ql-editor) {
      flex:                             1;
      height:                           100%;
      overflow-y:                       auto;
      overflow-wrap:                    anywhere;
      max-height:                       350px;
    }

    :deep(.ql-editor.ql-blank::before) {
      color:                            var(--Shades-60, #a0a0a0);
    }
  }

  .variables-section {
    flex:                               1;
    min-width:                          0;
    display:                            flex;
    flex-direction:                     column;
  }

  .variables-title {
    color:                              var(--Brand-Primary);
  }

  .full-height-editor {
    flex:                               1;
  }

  .header-variables {
    display:                            flex;
    align-items:                        start;
    justify-content:                    space-between;
    margin-bottom:                      10px;

    .btn-add-field {
      gap:                              8px;
    }
  }

  .variables-list {
    overflow-y:                         auto;
    overflow-x:                         hidden;
    display:                            flex;
    flex-direction:                     column;
    flex:                               1;
    gap:                                10px;
  }

  .variables-fixed {
    margin-bottom:                      10px;
  }

  .variable-item {
    display:                            flex;
    align-items:                        center;
    gap:                                10px;

    .field-select {
      width: 80%;
    }

    .section-info {
      width:                            100%;
      display:                          flex;
      align-items:                      center;
      justify-content:                  space-between;
    }
  }

  .tag-preview {
    width:                              35%;
    text-align:                         start;

    span {
      color:                            var(--Shades-70);
      cursor:                           copy;
    }
  }

  @media (max-width: 1500px) {
    .has-alert {
      .consent-template-box {
        // height:                       calc(100dvh - 459px); // replaced by flex: 1 in _scaffold.scss
      }
    }
  }

  @media (max-width: 991px) {
    .clinic-consent-template-edit {
      flex:                           none; // override global flex: 1 so height property takes effect
      height:                         calc(100dvh - 207px); // 207px = nav above component (AppMenu + clinic header + tabs)
    }

    .has-usage-bar {
      .clinic-consent-template-edit {
        height:                       calc(100dvh - 235px); // +28px MenuStorageBar
      }
    }

    .has-alert {
      .clinic-consent-template-edit {
        height:                       calc(100dvh - 290px); // +83px MenuUsageAlert(55) + MenuStorageBar(28)
      }
    }

    .consent-template-box {
      // height:                         calc(100dvh - 309px); // replaced by flex: 1
      overflow-y:                       scroll;
    }

    .section-inputs {
      flex-direction:                   column;
    }

    .section-template {
      flex-direction:                   column;
    }

    .editor-section,
    .variables-section {
      width:                            100%;
      flex:                             none;
    }

    .variables-section {
      max-height:                       unset;
    }

    .variables-list {
      overflow-y:                       visible;
      flex:                             none;
    }
  }

  @media (max-width: 767px) {
    .variable-item {
      flex-wrap:                        wrap;
      gap:                              10px 0;
      
      .field-select {
        width:                          100%
      }
    }
  }

  @media screen and (min-width: 992px) and (max-width: 1365px) {
    .consent-template-box {
      // height:                         calc(100dvh - 358px); // replaced by flex: 1 in _scaffold.scss
      // overflow-x:                     hidden; // handled by .inside-box
      // overflow-y:                     scroll; // handled by .inside-box
    }
  }
</style>
  
<script>
  import { useUserStore } from '@/store/user'
  import { TAG_DICTIONARY } from '@/utils/consentTagsDictionary';
  import BaseTutorialModal from '@/components/base/BaseTutorialModal.vue';
  import imgStep1 from '@/assets/images/tutorial/tutorialTemplaseConsent1.png';
  import imgStep2 from '@/assets/images/tutorial/tutorialTemplaseConsent2.png';
  import imgStep3 from '@/assets/images/tutorial/tutorialTemplaseConsent3.png';
  import imgStep4 from '@/assets/images/tutorial/tutorialTemplaseConsent4.png';
  
  const CONTENT_MAX_LENGTH = 20000

  const toolbarOptions = [
    [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
    ['bold', 'italic', 'underline'],
    [{ 'list': 'ordered'}, { 'list': 'bullet' }]
  ];
  
  export default {
    name: 'ClinicConsentTemplateEdit',
    props: {
      template_id: { type: [String, Number] },
    },
    components: {
      BaseTutorialModal
    },
    data: () => ({
      loading: false,
      isInitializing: true,
      editorOption: {
        readOnly: false,
        modules: {
          toolbar: toolbarOptions
        },
        theme: 'snow',
      },
      template: {
        id: null,
        name: null,
        description: null,
        htmlContent: null,
        isDefault: false
      },
      content: null,
      formSchema: [],
      required: false,
      hasFormChanged: false,
      changedFormItems: [],
      initialForm: {},
      rawVariableFields: [],
    }),
    watch: {
      template_id: {
        immediate: true,
        handler (template_id) {
          if (template_id && this.session) {
            this.fetchData()
          }
          this.$nextTick(() => {
            this.hasFormChanged = false
            this.changedFormItems = []
          })
        }, deep: true
      },
      template: {
        immediate: true,
        handler(newValue) {
          this.checkFormChange(newValue, 'template')
        }, deep: true
      },
      content: {
        immediate: true,
        handler(newValue) {
          this.checkFormChange(newValue, 'content')
        }, deep: true
      },
      formSchema: {
        immediate: true,
        handler(newValue) {
          this.checkFormChange(newValue, 'formSchema')
        }, deep: true
      },
      '$i18n.locale': {
        handler() {
          this.normalizeFormSchemaToCurrentLocale(this.formSchema);
          if (this.initialForm.formSchema) {
            this.normalizeFormSchemaToCurrentLocale(this.initialForm.formSchema);
          }
        }
      },
    },
    computed: {
      editor() {
        return this.$refs.quillEditor.quill;
      },
      contentMaxLength() {
        return CONTENT_MAX_LENGTH;
      },
      isContentTooLong() {
        return (this.content || '').length > CONTENT_MAX_LENGTH;
      },
      isGlobalClient() {
        const userStore = useUserStore();
        return userStore.sessionInfo?.isGlobalClient ?? false;
      },
      consentTutorialSteps() {
        return [
          {
            image: imgStep1,
            title: this.$t('tutorial.templaseConsent.step1.subTitle'),
            text: this.$t('tutorial.templaseConsent.step1.comtent')
          },
          {
            image: imgStep2,
            title: this.$t('tutorial.templaseConsent.step2.subTitle'),
            text: this.$t('tutorial.templaseConsent.step2.comtent')
          },
          {
            image: imgStep3,
            title: this.$t('tutorial.templaseConsent.step3.subTitle'),
            text: this.$t('tutorial.templaseConsent.step3.comtent')
          },
          {
            image: imgStep4,
            title: this.$t('tutorial.templaseConsent.step4.subTitle'),
            text: this.$t('tutorial.templaseConsent.step4.comtent'),
            warning: this.$t('tutorial.templaseConsent.step4.warning'),
          }
        ]
      },
      systemVariables() {
        return [
          {
            label: this.$t('templateConsentVariables.system.signatureImg'),
            tag: this.$t('consentTags.system.signatureImg')
          },
          {
            label: this.$t('templateConsentVariables.system.currentDate'),
            tag: this.$t('consentTags.system.currentDate')
          }
        ];
      },
      variableOptions() {
        return this.rawVariableFields.map(item => ({
          label: this.$t(`templateConsentVariables.${item.model}.${item.field}`),
          value: this.$t(`consentTags.${item.model}.${item.field}`),
        }));
      }
    },
    mounted() {
      const userStore = useUserStore();
      this.session = userStore.sessionInfo;

      if (this.template_id) {
        this.template.id = this.template_id
        this.fetchData()
      } else {
        this.$nextTick(() => {
          this.initialForm = _.cloneDeep({
            template: this.template,
            content: this.content,
            formSchema: this.formSchema,
          });
          this.isInitializing = false;
        });
      }

      this.fetchVariables()
    },
    methods: {
      checkFormChange(value, obj) {
        if (this.isInitializing) return;

        let hasObj = this.changedFormItems.includes(obj)
        if (JSON.stringify(value) === JSON.stringify(this.initialForm[obj])) {
          if (hasObj) {
            let index = this.changedFormItems.indexOf(obj)
            this.changedFormItems.splice(index, 1)
          }
          if (this.changedFormItems.length > 0) return
          this.hasFormChanged = false
        } else {
          if (!hasObj) this.changedFormItems.push(obj)
          this.hasFormChanged = true
        }
      },
      async fetchData() {
        this.loading = true
        NProgress.start()
        try {
          let response = await this.$clinicService.getConsentTemplate({ 
            clinicID: this.session.currentClinic.id,
            templateID: this.template_id 
          });
          const result = response.result
          this.setTemplate(result)
        } catch (err) {
          this.$message.popup(this, 'request_error', 'operationFail', 'error')
        } finally {
          NProgress.done(true)
          this.loading = false
        }
      },
      setTemplate(result) {
        this.isInitializing = true;

        this.template = result;
        this.content = String(result.htmlContent);

        this.formSchema = result.formSchema
        ? JSON.parse(JSON.stringify(result.formSchema))
        : [];

        this.normalizeFormSchemaToCurrentLocale();

        this.$refs.quillEditor.setHTML(this.content);

        this.$nextTick(() => {
          this.content = this.$refs.quillEditor.getHTML();

          const quillInstance = this.$refs.quillEditor.getQuill 
            ? this.$refs.quillEditor.getQuill() 
            : this.$refs.quillEditor.quill;

          if (quillInstance) {
            quillInstance.enable(!result.isDefault);
          }
          
          this.autoDetectSchemaFromContent();

          this.initialForm = _.cloneDeep({
            template: this.template,
            content: this.content,
            formSchema: this.formSchema,
          });

          this.isInitializing = false;
        });
      },
      normalizeFormSchemaToCurrentLocale(schema = this.formSchema) {
        if (!Array.isArray(schema)) return;

        schema.forEach(item => {
          if (!item.key) return;

          const canonicalPath = TAG_DICTIONARY[item.key];
          if (!canonicalPath) return; // token não reconhecido, não mexe

          const [model, field] = canonicalPath.split('.');
          const newTag = this.$t(`consentTags.${model}.${field}`);

          if (newTag !== item.key) {
            item.key = newTag;
          }
        });
      },
      autoDetectSchemaFromContent() {
        if (!this.content || this.variableOptions.length === 0) return;

        const regex = /\{\{([\w_.]+)\}\}/g;
        const detectedTags = new Set();
        let match;
        
        while ((match = regex.exec(this.content)) !== null) {
          detectedTags.add(match[1]); 
        }

        const validTags = Array.from(detectedTags).filter(tag => 
          this.variableOptions.some(opt => opt.value === tag)
        );

        validTags.forEach(tag => {
          const isAlreadyInSchema = this.formSchema.some(item => item.key === tag);
          if (!isAlreadyInSchema) {
            this.formSchema.push({ key: tag });
          }
        });
      },
      async doSave() {
        if (this.isContentTooLong) {
          return;
        }

        const hasSignatureTag = this.$i18n.availableLocales.some(locale => {
          const tag = this.$i18n.getLocaleMessage(locale)?.consentTags?.system?.signatureImg;
          return tag && this.content?.includes(`{{${tag}}}`);
        });

        if (!hasSignatureTag) {
          this.$message.popup(this, 'clinic.missingSignatureTagError', 'operationFail', 'error');
          return;
        }

        let params = {
          clinicID: this.session.currentClinic.id,
          template: {
            name: this.template.name.trim(),
            description: (this.template.description || '').trim(),
            htmlContent: this.content,
            formSchema: this.formSchema,
          }
        }
  
        if (this.template.id) {
          params.template.id = this.template.id
        }
  
        this.loading = true
        NProgress.start()
        try {
          await this.$clinicService.saveConsentTemplate(params);
          this.$message.popup(this, 'general.createdSuccess', 'operationSuccess', 'success')
          this.$emit('cancel-edit')
        } catch(err) {
          this.$message.popup(this, 'general.createdError', 'operationFail', 'error')
        } finally {
          NProgress.done(true)
          this.loading = false
        }
        
      },
      addField() {
        this.formSchema.push({ key: null })
      },
      removeField(item) {
        const index = this.formSchema.indexOf(item);
        if (index > -1) {
          this.formSchema.splice(index, 1);
        }
      },
      async fetchVariables() {
        this.loading = true;

        try {
          const response = await this.$clinicService.getConsentVariables();

          if (response && response.result) {
            this.rawVariableFields = response.result;
          }
        } catch (err) {
          this.$message.popup(this, 'request_error', 'operationFail', 'error');
        } finally {
          this.loading = false;
          this.autoDetectSchemaFromContent();
        }
      },
      async copyToClipboard(text) {
        if (!text || text === '{{null}}' || text === '-' || text === '{{}}') return;

        try {
          await navigator.clipboard.writeText(text);
          this.$message.popup(this, 'clinic.copiedClipboard', 'operationSuccess', 'success');
        } catch (err) {
          this.$message.popup(this, 'clinc.errorCopyingClipboard', 'operationFail', 'error');
        }
      },
    },
  }
</script>